import type { ServiceAttributionDto } from '@repo/contracts';

import { consentState, subscribeConsent } from './google-ads';

/**
 * The Google Ads click id (`gclid`, or `gbraid`/`wbraid` from iOS) and UTM
 * tags on the landing URL, sent with the intake so a paid audit can later be
 * uploaded to Google Ads against the click that brought it.
 *
 * Kept in memory for the visit, which covers the usual landing → form →
 * submit. Written to the device (localStorage, 90 days — Google's click
 * window) only once the visitor accepts ad cookies, and wiped if they reject.
 * A returning visitor who never chose falls back to Google's own `_gcl_aw`
 * cookie, which the tag only writes where ad storage is granted by region.
 */

const STORAGE_KEY = 'floto-ad-attribution';
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

type Attribution = ServiceAttributionDto;

const PARAMS: [string, keyof Attribution][] = [
  ['gclid', 'gclid'],
  ['gbraid', 'gbraid'],
  ['wbraid', 'wbraid'],
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_content', 'utmContent'],
  ['utm_term', 'utmTerm'],
];

let landing: Attribution | null = null;

function persist(a: Attribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(a));
  } catch {
    // Storage blocked: memory still covers this visit.
  }
}

function forget(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, or nothing we can reach.
  }
}

function readStored(now: number): Attribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const a = JSON.parse(raw) as Attribution;
    const at = Date.parse(a.clickIdCapturedAt ?? '');
    if (!Number.isFinite(at) || now - at > TTL_MS) {
      forget();
      return null;
    }
    return a;
  } catch {
    return null;
  }
}

/** `_gcl_aw` is `GCL.<unix seconds>.<gclid>`. */
function fromGoogleCookie(now: number): Attribution | null {
  const match = /(?:^|;\s*)_gcl_aw=([^;]+)/.exec(document.cookie);
  if (!match) return null;
  const [prefix, seconds, ...rest] = decodeURIComponent(match[1]!).split('.');
  const gclid = rest.join('.');
  const at = Number(seconds) * 1000;
  if (prefix !== 'GCL' || !gclid || !Number.isFinite(at) || now - at > TTL_MS) {
    return null;
  }
  return { clickIdCapturedAt: new Date(at).toISOString(), gclid };
}

/** Reads the landing URL. Called once at startup. */
export function captureLanding(
  search = window.location.search,
  now = new Date(),
): void {
  const params = new URLSearchParams(search);
  const found: Attribution = {};
  for (const [param, key] of PARAMS) {
    const value = params.get(param)?.trim();
    if (value) found[key] = value.slice(0, key.startsWith('utm') ? 200 : 512);
  }
  if (Object.keys(found).length === 0) return;
  if (found.gclid || found.gbraid || found.wbraid) {
    found.clickIdCapturedAt = now.toISOString();
  }
  landing = found;
  if (consentState().choice === 'granted' && found.clickIdCapturedAt) {
    persist(found);
  }
}

let unsubscribe: (() => void) | null = null;

/** Stores or wipes the landing click as the visitor accepts or rejects. */
export function initAttribution(): void {
  captureLanding();
  unsubscribe?.();
  unsubscribe = subscribeConsent(() => {
    const { choice } = consentState();
    if (choice === 'denied') forget();
    if (choice === 'granted' && landing?.clickIdCapturedAt) persist(landing);
  });
}

/**
 * What the intake sends: this visit's landing tags, else a stored click from
 * an earlier visit, else Google's cookie. Nothing from the device once the
 * visitor has rejected ad cookies.
 */
export function attributionForRequest(
  now = Date.now(),
): Attribution | undefined {
  if (landing) return landing;
  if (consentState().choice === 'denied') return undefined;
  return readStored(now) ?? fromGoogleCookie(now) ?? undefined;
}

/** Test seam: forget this visit's landing. */
export function resetAttributionForTests(): void {
  landing = null;
  unsubscribe?.();
  unsubscribe = null;
}
